import { toast } from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';

import { request } from '@core/api/request';
import { queryClient } from '@core/api/query-client';
import { QUERY_KEYS } from '@src/shared/constants/query-keys';

import type { TLeadStatus } from '@shared/types/lead-status';

export const orderLeadReferal = async (ids: number[]) => {
  return await request({
    url: 'lead-referals/reorder-referals/',
    method: 'POST',
    body: { ids },
  });
};

export const useOrderLeadReferals = () => {
  return useMutation({
    mutationFn: orderLeadReferal,
    onMutate: async (newIds: number[]) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.getReferList],
      });
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEYS.getInfiniteReferList, {}],
      });

      // Snapshot the previous values
      const previousReferList = queryClient.getQueryData([
        QUERY_KEYS.getReferList,
      ]);
      const previousInfiniteReferList = queryClient.getQueryData([
        QUERY_KEYS.getInfiniteReferList,
        {},
      ]);

      // Optimistically update the infinite refer list
      queryClient.setQueryData(
        [QUERY_KEYS.getInfiniteReferList, {}],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (old: any) => {
          if (!old?.pages) return old;

          return {
            ...old,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pages: old.pages.map((page: any) => {
              if (!page.results) return page;

              const sortedResults = [...page.results].sort(
                (a: TLeadStatus, b: TLeadStatus) => {
                  return newIds.indexOf(a.id) - newIds.indexOf(b.id);
                }
              );

              return {
                ...page,
                results: sortedResults,
              };
            }),
          };
        }
      );

      // Return a context object with the snapshotted values
      return { previousReferList, previousInfiniteReferList };
    },
    onError: (error: string) => {
      toast.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.getReferList],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.getInfiniteReferList],
      });
    },
  });
};
